/**
 * End User License Agreement — static legal page, promised to Intuit as part
 * of the QuickBooks Online app-review submission. Registered at exactly
 * /legal/eula (see AppRoutes.tsx). Rendered OUTSIDE the ClickPay auth wall
 * (see App.tsx) — Intuit's review team and end users must be able to load
 * this without a ClickPay session.
 *
 * ClickPay is a single-operator internal operations tool (not a public
 * SaaS product) built for BetterBasics/MeasuRite. This is plain-English
 * content, not a lawyer-drafted contract — kept intentionally simple to
 * match what the app actually is.
 */
export default function LegalEula() {
  return (
    <div className="min-h-screen bg-gray-50 flex justify-center px-4 py-12">
      <div className="w-full max-w-2xl">
        <div className="text-center mb-8">
          <h1 className="text-xl font-semibold text-gray-900">ClickPay</h1>
          <p className="text-xs text-gray-500 mt-0.5">End User License Agreement</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-8 text-sm text-gray-700 leading-relaxed space-y-5">
          <p className="text-xs text-gray-400">Last updated: July 2026</p>

          <p>
            ClickPay is an internal software tool built and operated by
            BetterBasics (MeasuRite) to run its own Amazon marketplace
            operations, advertising (PPC), and accounting — including a
            QuickBooks Online integration used to reconcile supplier
            payments. It is not offered to the public and is not a
            commercial SaaS product. This agreement covers use of ClickPay
            by BetterBasics/MeasuRite personnel and authorized service
            accounts.
          </p>

          <section>
            <h2 className="text-sm font-semibold text-gray-900 mb-1">
              1. Internal use only
            </h2>
            <p>
              ClickPay is licensed solely for BetterBasics/MeasuRite's own
              internal business operations. It is not licensed, resold, or
              made available to any other company or the general public.
              Access is limited to people and systems the app owner
              authorizes.
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-900 mb-1">
              2. What the software does
            </h2>
            <p>
              ClickPay connects to services BetterBasics/MeasuRite already
              uses — including Amazon Seller/Advertising APIs and
              QuickBooks Online — to pull operational data (orders, ad
              performance, inventory, bills, and payments) into one place,
              and in some cases to take actions on those systems on the
              operator's behalf (for example, adjusting an ad bid or
              recording a reconciled payment).
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-900 mb-1">
              3. Provided as-is, no warranty
            </h2>
            <p>
              ClickPay is provided "as is," without warranty of any kind,
              express or implied — including no warranty of accuracy,
              uptime, or fitness for a particular purpose. It is an
              internal tool under active development; features can change
              or break without notice. The app owner is not liable for any
              loss arising from its use, including from data errors,
              downtime, or automated actions it takes against connected
              third-party services.
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-900 mb-1">
              4. Data use
            </h2>
            <p>
              Data ClickPay retrieves from connected accounts (including
              QuickBooks Online) is used solely to power ClickPay's own
              features — dashboards, reconciliation, reporting, and
              automation — for BetterBasics/MeasuRite's internal use. See
              the{' '}
              <a href="/legal/privacy" className="text-brand-600 hover:underline">
                Privacy Policy
              </a>{' '}
              for details on what is retrieved and how it's stored.
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-900 mb-1">
              5. Changes
            </h2>
            <p>
              This agreement may be updated as the tool evolves. Continued
              use of ClickPay after an update means you accept the current
              version.
            </p>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-900 mb-1">
              6. Contact
            </h2>
            <p>
              Questions about this agreement — contact the app owner.
              There is no dedicated support team; ClickPay is maintained
              directly by BetterBasics/MeasuRite.
            </p>
          </section>
        </div>
      </div>
    </div>
  )
}
